import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { authApi } from "../../api/auth";
import { securityApi } from "../../api/security";
import { useAuth } from "../../context/AuthContext";
import {
  AUTH_BRAND,
  AuthPasswordField,
  authPrimaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

const EVENT_LABELS = {
  probe_blocked: "Probe bloqueado",
  auth_success: "Inicio de sesión correcto",
  auth_failure: "Intento de acceso fallido",
  access_denied: "Acceso rechazado",
  sensitive_read: "Lectura de datos sensibles",
  sensitive_write: "Cambio sobre datos sensibles",
  password_changed: "Contraseña cambiada",
  password_reset: "Contraseña restablecida",
  sessions_revoked: "Sesiones anteriores cerradas",
};

const SEVERITY_COLOR = {
  critical: "error",
  warning: "warning",
  success: "success",
  info: "default",
};

function formatDate(value) {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function total(summary, key) {
  return Number(summary?.[key] || 0);
}

function Metric({ label, value, helper }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "1.35rem", lineHeight: 1, fontWeight: 950, color: AUTH_BRAND.dark }}>
        {Number(value || 0).toLocaleString("es-AR")}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.55, fontWeight: 800, color: AUTH_BRAND.dark }}>
        {label}
      </Typography>
      {helper ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.2, color: "text.secondary" }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function EventRows({ rows = [], emptyText }) {
  if (!rows.length) {
    return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
  }

  return (
    <Stack divider={<Divider flexItem />}>
      {rows.map((event) => (
        <Box key={event.id} sx={{ py: 1.25 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "flex-start" }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 900, color: AUTH_BRAND.dark }}>
                  {EVENT_LABELS[event.kind] || event.kind}
                </Typography>
                <Chip
                  size="small"
                  color={SEVERITY_COLOR[event.severity] || "default"}
                  variant={event.severity === "warning" || event.severity === "critical" ? "filled" : "outlined"}
                  label={event.count > 1 ? `${event.count} eventos` : event.severity || "info"}
                  sx={{ height: 22, fontSize: ".68rem", fontWeight: 850 }}
                />
              </Stack>
              <Typography variant="caption" sx={{ display: "block", mt: 0.45, color: "text.secondary", overflowWrap: "anywhere" }}>
                {[event.request?.method, event.request?.pathClass, event.metadata?.area].filter(Boolean).join(" · ") || "Evento de seguridad"}
              </Typography>
              {event.request?.requestId ? (
                <Typography variant="caption" sx={{ display: "block", mt: 0.15, color: "text.disabled", overflowWrap: "anywhere" }}>
                  request {event.request.requestId}
                </Typography>
              ) : null}
            </Box>
            <Typography variant="caption" sx={{ flex: "0 0 auto", color: "text.secondary" }}>
              {formatDate(event.lastSeenAt)}
            </Typography>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export default function AccountSecurityPage() {
  const { user } = useAuth();
  const [form, setForm] = React.useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [securityLoading, setSecurityLoading] = React.useState(true);
  const [securityError, setSecurityError] = React.useState("");
  const [overview, setOverview] = React.useState(null);
  const [revoking, setRevoking] = React.useState(false);

  const loadSecurity = React.useCallback(async () => {
    try {
      setSecurityLoading(true);
      setSecurityError("");
      const data = await securityApi.overview({ days: 30, limit: 24 });
      setOverview(data);
    } catch (err) {
      setSecurityError(err.message || "No se pudo cargar el seguimiento de seguridad.");
    } finally {
      setSecurityLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadSecurity();
  }, [loadSecurity]);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.currentPassword) return setError("Ingresá tu contraseña actual.");
    if (form.newPassword.length < 8) return setError("La nueva contraseña debe tener al menos 8 caracteres.");
    if (form.newPassword !== form.confirmPassword) return setError("Las contraseñas nuevas no coinciden.");
    if (form.currentPassword === form.newPassword) return setError("La nueva contraseña debe ser diferente de la actual.");

    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await authApi.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      setMessage(data?.message || "Contraseña cambiada correctamente.");
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      await loadSecurity();
    } catch (err) {
      setError(err.message || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    const confirmed = typeof window === "undefined" || window.confirm("¿Cerrar todas las demás sesiones abiertas de tu cuenta?");
    if (!confirmed) return;
    try {
      setRevoking(true);
      setSecurityError("");
      const data = await securityApi.revokeOtherSessions();
      setMessage(data?.message || "Las demás sesiones fueron cerradas.");
      await loadSecurity();
    } catch (err) {
      setSecurityError(err.message || "No se pudieron cerrar las demás sesiones.");
    } finally {
      setRevoking(false);
    }
  };

  const account = overview?.account || {};
  const platform = overview?.platform || null;
  const accountSummary = account.summary || {};
  const platformSummary = platform?.summary || {};
  const protectionSignals = [
    account.state?.emailVerified,
    Boolean(account.state?.lastLoginAt),
    total(accountSummary, "auth_failure") === 0,
  ];
  const protectionScore = Math.round((protectionSignals.filter(Boolean).length / protectionSignals.length) * 100);

  return (
    <Container maxWidth="md" disableGutters>
      <Stack spacing={{ xs: 1.5, sm: 2.25 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="space-between" alignItems={{ sm: "center" }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ width: 46, height: 46, flex: "0 0 auto", borderRadius: 3, display: "grid", placeItems: "center", color: AUTH_BRAND.orange, bgcolor: "rgba(243,90,58,.1)" }}>
              <SecurityRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ color: AUTH_BRAND.dark, fontWeight: 950 }}>Seguridad</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>{user?.email || "Tu cuenta"}</Typography>
            </Box>
          </Stack>
          <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={loadSecurity} disabled={securityLoading} sx={{ textTransform: "none", fontWeight: 850, alignSelf: { xs: "flex-start", sm: "center" } }}>
            Actualizar seguimiento
          </Button>
        </Stack>

        {securityLoading ? <LinearProgress /> : null}
        {securityError ? <Alert severity="warning">{securityError}</Alert> : null}
        {message ? <Alert severity="success">{message}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
            <Stack spacing={1.8}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
                <Box>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <VerifiedUserRoundedIcon fontSize="small" color="success" />
                    <Typography variant="h6" sx={{ fontWeight: 950, color: AUTH_BRAND.dark }}>Protección de la cuenta</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    Señales básicas de autenticación y sesiones. El historial no guarda contraseñas, tokens ni IPs completas.
                  </Typography>
                </Box>
                <Chip label={`${protectionScore}% señales OK`} color={protectionScore === 100 ? "success" : "warning"} sx={{ fontWeight: 900 }} />
              </Stack>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
                <Metric label="Email" value={account.state?.emailVerified ? 1 : 0} helper={account.state?.emailVerified ? "verificado" : "sin verificar"} />
                <Metric label="Accesos fallidos" value={total(accountSummary, "auth_failure")} helper="últimos 30 días" />
                <Metric label="Accesos rechazados" value={total(accountSummary, "access_denied")} helper="últimos 30 días" />
                <Metric label="Último ingreso" value={account.state?.lastLoginAt ? 1 : 0} helper={formatDate(account.state?.lastLoginAt)} />
              </Box>

              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="outlined" startIcon={revoking ? <WebProfitSpinner size={16} /> : <LogoutRoundedIcon />} disabled={revoking} onClick={handleRevoke} sx={{ textTransform: "none", fontWeight: 850 }}>
                  {revoking ? "Cerrando..." : "Cerrar las demás sesiones"}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: { sm: "center" } }}>
                  Tu sesión actual permanece abierta.
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {platform ? (
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
              <Stack spacing={1.8}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 950, color: AUTH_BRAND.dark }}>Seguridad de datos · plataforma</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    Resumen agregado de los últimos {platform.windowDays || 7} días. Los scanners repetidos se agrupan para no convertir la auditoría en carga innecesaria.
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 2 }}>
                  <Metric label="Probes bloqueados" value={total(platformSummary, "probe_blocked")} />
                  <Metric label="Login fallidos" value={total(platformSummary, "auth_failure")} />
                  <Metric label="Accesos rechazados" value={total(platformSummary, "access_denied")} />
                  <Metric label="Lecturas sensibles" value={total(platformSummary, "sensitive_read")} />
                  <Metric label="Orígenes observados" value={platform.uniqueSources || 0} />
                </Box>
                {platform.criticalCount > 0 ? (
                  <Alert severity="error">Hay {platform.criticalCount} eventos críticos registrados en la ventana actual.</Alert>
                ) : (
                  <Alert severity="success">No hay eventos críticos registrados en la ventana actual.</Alert>
                )}
                <EventRows rows={platform.recentEvents?.slice(0, 12)} emptyText="Todavía no hay eventos de plataforma para mostrar." />
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
            <Stack spacing={1.2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, color: AUTH_BRAND.dark }}>Actividad de seguridad de tu cuenta</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  Eventos agregados por ventana para conservar trazabilidad sin almacenar contenido de las solicitudes.
                </Typography>
              </Box>
              <EventRows rows={account.recentEvents} emptyText="No hay eventos de seguridad registrados todavía." />
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={1.75}>
              <Box>
                <Typography variant="h6" sx={{ color: AUTH_BRAND.dark, fontWeight: 950 }}>Cambiar contraseña</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>Las demás sesiones abiertas se cerrarán por seguridad.</Typography>
              </Box>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={1.5}>
                  <AuthPasswordField label="Contraseña actual" name="currentPassword" value={form.currentPassword} onChange={handleChange} autoComplete="current-password" disabled={loading} inputProps={{ minLength: undefined }} />
                  <AuthPasswordField label="Nueva contraseña" name="newPassword" value={form.newPassword} onChange={handleChange} autoComplete="new-password" disabled={loading} helperText="Mínimo 8 caracteres y distinta de la actual." />
                  <AuthPasswordField label="Repetir contraseña" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} autoComplete="new-password" disabled={loading} />
                  <Button type="submit" disabled={loading} variant="contained" size="large" startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={{ ...authPrimaryButtonSx, width: { xs: "100%", sm: "auto" }, alignSelf: { sm: "flex-start" } }}>
                    {loading ? "Guardando..." : "Cambiar contraseña"}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
